import React, { createContext, useContext, useReducer } from "react";

// ── Types ────────────────────────────────────────────────────────────────

export interface BlockSettings {
  variant: "compact" | "detailed";
  headingOverride: string | null;
  columnsVisible: string[];
  // Image block
  imageKey?: string | null;
  imageWidth?: number;
  imageAlign?: "left" | "center" | "right";
  // Signature block
  signatureLabel?: string;
  signatureShowDate?: boolean;
  signatureShowName?: boolean;
}

export interface BlockConfig {
  id: string;
  blockType: string;
  enabled: boolean;
  locked: boolean;
  order: number;
  settings: BlockSettings;
}

export interface LineItem {
  kind: "time" | "fee" | "disbursement" | "tax" | "credit" | "discount";
  feeItemId?: string | null;
  description: string;
  quantity: number;
  unitPriceCents: number;
  amountCents: number;
  currency?: string;
  timeEntryIds?: string[];
  discount?: { type: string | null; percent: number | null; amountCents: number | null };
  taxable: boolean;
  taxAmountCents: number;
}

export interface Totals {
  subtotalCents: number;
  discountCents: number;
  taxCents: number;
  totalCents: number;
  paidCents: number;
  balanceDueCents: number;
}

export interface BuilderState {
  documentType: "invoice" | "account_summary";
  documentId: string;
  blocks: BlockConfig[];
  selectedBlockId: string | null;
  // Invoice-specific
  lineItems: LineItem[];
  invoiceNumber: string;
  dueAt: string | null;
  // Summary-specific
  invoices: any[];
  payments: any[];
  matterBreakdowns: any[];
  // Shared
  client: any;
  matter: any | null;
  firm: any;
  feeItems: any[];
  unbilledTimeEntries: any[];
  existingPayments: any[];
  totals: Totals;
  isDirty: boolean;
  isSaving: boolean;
  notesToClient: string;
  internalNotes: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────

let _blockIdCounter = 0;
export function generateBlockId(): string {
  _blockIdCounter += 1;
  return `block-${Date.now()}-${_blockIdCounter}`;
}

/** Ensure every block in an array has an id (backwards compat with old data) */
function ensureBlockIds(blocks: BlockConfig[]): BlockConfig[] {
  return blocks.map((b) => ({
    ...b,
    id: b.id || generateBlockId(),
  }));
}

// ── Actions ──────────────────────────────────────────────────────────────

type BuilderAction =
  | { type: "INIT"; payload: Partial<BuilderState> }
  | { type: "SET_BLOCKS"; blocks: BlockConfig[] }
  | { type: "TOGGLE_BLOCK"; blockId: string }
  | { type: "REORDER_BLOCKS"; blocks: BlockConfig[] }
  | { type: "UPDATE_BLOCK_SETTINGS"; blockId: string; settings: Partial<BlockSettings> }
  | { type: "SELECT_BLOCK"; blockId: string | null }
  | { type: "ADD_BLOCK"; block: BlockConfig }
  | { type: "REMOVE_BLOCK"; blockId: string }
  | { type: "SET_LINE_ITEMS"; lineItems: LineItem[] }
  | { type: "ADD_LINE_ITEM"; item: LineItem }
  | { type: "UPDATE_LINE_ITEM"; index: number; item: Partial<LineItem> }
  | { type: "REMOVE_LINE_ITEM"; index: number }
  | { type: "REORDER_LINE_ITEMS"; lineItems: LineItem[] }
  | { type: "SET_NOTES"; field: "notesToClient" | "internalNotes"; value: string }
  | { type: "SET_SAVING"; isSaving: boolean }
  | { type: "MARK_CLEAN" };

function computeTotals(lineItems: LineItem[], paidCents: number): Totals {
  const subtotalCents = lineItems
    .filter((li) => !["tax", "credit", "discount"].includes(li.kind))
    .reduce((sum, li) => sum + li.amountCents, 0);
  const discountCents = lineItems
    .filter((li) => li.kind === "discount")
    .reduce((sum, li) => sum + Math.abs(li.amountCents), 0);
  const taxCents = lineItems.reduce((sum, li) => sum + (li.taxAmountCents || 0), 0);
  const totalCents = subtotalCents - discountCents + taxCents;
  const balanceDueCents = totalCents - paidCents;
  return { subtotalCents, discountCents, taxCents, totalCents, paidCents, balanceDueCents };
}

function builderReducer(state: BuilderState, action: BuilderAction): BuilderState {
  switch (action.type) {
    case "INIT": {
      const payload = { ...action.payload };
      if (payload.blocks) {
        payload.blocks = ensureBlockIds(payload.blocks);
      }
      return { ...state, ...payload, isDirty: false, selectedBlockId: null };
    }
    case "SET_BLOCKS":
      return { ...state, blocks: action.blocks, isDirty: true };
    case "TOGGLE_BLOCK":
      return {
        ...state,
        blocks: state.blocks.map((b) =>
          b.id === action.blockId && !b.locked
            ? { ...b, enabled: !b.enabled }
            : b
        ),
        isDirty: true,
      };
    case "REORDER_BLOCKS":
      return { ...state, blocks: action.blocks.map((b, i) => ({ ...b, order: i })), isDirty: true };
    case "UPDATE_BLOCK_SETTINGS":
      return {
        ...state,
        blocks: state.blocks.map((b) =>
          b.id === action.blockId
            ? { ...b, settings: { ...b.settings, ...action.settings } }
            : b
        ),
        isDirty: true,
      };
    case "SELECT_BLOCK":
      return { ...state, selectedBlockId: action.blockId };
    case "ADD_BLOCK": {
      const blocks = [...state.blocks, { ...action.block, order: state.blocks.length }];
      return { ...state, blocks, isDirty: true };
    }
    case "REMOVE_BLOCK": {
      const blocks = state.blocks
        .filter((b) => b.id !== action.blockId || b.locked)
        .map((b, i) => ({ ...b, order: i }));
      return { ...state, blocks, selectedBlockId: null, isDirty: true };
    }
    case "SET_LINE_ITEMS": {
      const totals = computeTotals(action.lineItems, state.totals.paidCents);
      return { ...state, lineItems: action.lineItems, totals, isDirty: true };
    }
    case "ADD_LINE_ITEM": {
      const lineItems = [...state.lineItems, action.item];
      const totals = computeTotals(lineItems, state.totals.paidCents);
      return { ...state, lineItems, totals, isDirty: true };
    }
    case "UPDATE_LINE_ITEM": {
      const lineItems = state.lineItems.map((li, i) =>
        i === action.index ? { ...li, ...action.item } : li
      );
      const totals = computeTotals(lineItems, state.totals.paidCents);
      return { ...state, lineItems, totals, isDirty: true };
    }
    case "REMOVE_LINE_ITEM": {
      const lineItems = state.lineItems.filter((_, i) => i !== action.index);
      const totals = computeTotals(lineItems, state.totals.paidCents);
      return { ...state, lineItems, totals, isDirty: true };
    }
    case "REORDER_LINE_ITEMS": {
      const totals = computeTotals(action.lineItems, state.totals.paidCents);
      return { ...state, lineItems: action.lineItems, totals, isDirty: true };
    }
    case "SET_NOTES":
      return { ...state, [action.field]: action.value, isDirty: true };
    case "SET_SAVING":
      return { ...state, isSaving: action.isSaving };
    case "MARK_CLEAN":
      return { ...state, isDirty: false };
    default:
      return state;
  }
}

// ── Context ──────────────────────────────────────────────────────────────

const initialState: BuilderState = {
  documentType: "invoice",
  documentId: "",
  blocks: [],
  selectedBlockId: null,
  lineItems: [],
  invoiceNumber: "",
  dueAt: null,
  invoices: [],
  payments: [],
  matterBreakdowns: [],
  client: null,
  matter: null,
  firm: null,
  feeItems: [],
  unbilledTimeEntries: [],
  existingPayments: [],
  totals: { subtotalCents: 0, discountCents: 0, taxCents: 0, totalCents: 0, paidCents: 0, balanceDueCents: 0 },
  isDirty: false,
  isSaving: false,
  notesToClient: "",
  internalNotes: "",
};

interface BuilderContextValue {
  state: BuilderState;
  dispatch: React.Dispatch<BuilderAction>;
}

const BuilderContext = createContext<BuilderContextValue>({
  state: initialState,
  dispatch: () => {},
});

export const BuilderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(builderReducer, initialState);
  return (
    <BuilderContext.Provider value={{ state, dispatch }}>
      {children}
    </BuilderContext.Provider>
  );
};

export const useBuilder = () => useContext(BuilderContext);

export default BuilderContext;
